<template>
  <span />
</template>

<script>
import { AddressFinder } from "@ideal-postcodes/address-finder-bundled";
import { COUNTRY_CODE } from "../util/constants";
import { isIdealPostcodesEnabled } from "../util/ideal-postcodes";

const IDPC_QUERY_TAGS = [
  `env:${process.env.VUE_APP_ENV}`,
  "com-app:query",
].join(",");

const IDPC_RESOLVE_TAGS = [
  `env:${process.env.VUE_APP_ENV}`,
  "com-app:resolve",
].join(",");

function formatIdealPostcodesAddress(address) {
  return [
    address?.line_1,
    address?.line_2,
    address?.line_3,
    address?.post_town,
    address?.postcode,
  ]
    .filter(Boolean)
    .map((s) => s.trim())
    .join(", ");
}

export default {
  name: "IdealPostcodesAutoComplete",
  props: {
    value: { type: String, default: "" },
    checked: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    inputId: { type: String, default: "" },
    addressMode: {
      type: String,
      default: "flat_number",
      validator: (value) => ["flat_number", "full_address"].includes(value),
    },
    defaultCountryIso3: { type: String, default: null },
  },
  data() {
    return {
      addressFinder: null,
      afAttached: false,
      mode: "auto", // auto | manualAwaitRefocus | manualEditing | manualBlurred
      suppressNextMetaReset: false,
      manualEntryId: `manual-entry-${this._uid}`,
      idealPostcodesInputClass: "idpc-enabled-input",
      inputBlurHandler: null,
      documentFocusInHandler: null,
    };
  },
  computed: {
    resolvedInputId() {
      return this.inputId || `lookup-address-${this._uid}`;
    },
    resolvedPostcodeOption() {
      const postcode = this.$store?.state?.project?.address?.postcode;
      if (typeof postcode !== "string") return undefined;
      const trimmed = postcode.trim();
      return trimmed ? { postcode: trimmed } : undefined;
    },
    resolvedDefaultCountry() {
      if (this.defaultCountryIso3) {
        return this.defaultCountryIso3;
      }
      const siteCountry = this.$store?.state?.project?.address?.country;

      // Ideal Postcodes expects ISO-3 format
      const COUNTRY_CODE_ISO_3 = {
        [COUNTRY_CODE["United Kingdom"]]: "GBR",
        [COUNTRY_CODE["Australia"]]: "AUS",
        [COUNTRY_CODE["United States"]]: "USA",
        [COUNTRY_CODE["New Zealand"]]: "NZL",
        [COUNTRY_CODE["Germany"]]: "DEU",
      };
      return COUNTRY_CODE_ISO_3[siteCountry] || null;
    },
  },
  watch: {
    value() {
      if (this.suppressNextMetaReset) {
        this.suppressNextMetaReset = false;
        return;
      }
      if (this.addressMode !== "flat_number") {
        return;
      }
      this.emitNullMeta();
    },
    checked(value) {
      if (!value) {
        this.detachFinder();
        this.mode = "auto";
        if (this.addressMode === "flat_number") {
          this.emitNullMeta();
        }
      } else if (!this.disabled && !this.afAttached) {
        this.reattachFinder();
      }
    },
    disabled(value) {
      if (value) {
        this.detachFinder();
        this.mode = "auto";
      }
    },
  },
  mounted() {
    if (!isIdealPostcodesEnabled()) {
      return;
    }
    this.setupAddressFinder();
    const input = this.getInputElement();
    if (input) {
      this.inputBlurHandler = () => this.handleInputBlur();
      input.addEventListener("blur", this.inputBlurHandler);
    }
    this.documentFocusInHandler = (event) => this.handleDocumentFocusIn(event);
    document.addEventListener("focusin", this.documentFocusInHandler);
  },
  beforeDestroy() {
    const input = this.getInputElement();
    if (input && this.inputBlurHandler) {
      input.removeEventListener("blur", this.inputBlurHandler);
    }
    if (this.documentFocusInHandler) {
      document.removeEventListener("focusin", this.documentFocusInHandler);
    }
    this.unmarkIdealPostcodesInput();
    this.detachFinder();
  },
  methods: {
    markIdealPostcodesInput() {
      const hostElement = document.getElementById(this.resolvedInputId);
      hostElement?.classList.add(this.idealPostcodesInputClass);
    },
    unmarkIdealPostcodesInput() {
      const hostElement = document.getElementById(this.resolvedInputId);
      hostElement?.classList.remove(this.idealPostcodesInputClass);
    },
    isTargetInput(event) {
      const target = event?.target;
      const targetInputElement = this.getInputElement();
      const activeElement = document.activeElement;
      return Boolean(
        targetInputElement &&
          (target === targetInputElement ||
            targetInputElement.contains(target) ||
            activeElement === targetInputElement ||
            targetInputElement.contains(activeElement))
      );
    },
    handleDocumentFocusIn(event) {
      if (!this.isTargetInput(event)) {
        return;
      }
      if (this.mode === "manualAwaitRefocus") {
        this.mode = "manualEditing";
        return;
      }
      if (
        this.mode !== "manualBlurred" ||
        this.afAttached ||
        !this.checked ||
        this.disabled
      ) {
        return;
      }
      this.reattachFinder();
      this.mode = "auto";
    },
    handleInputBlur() {
      if (this.mode === "manualEditing") {
        this.mode = "manualBlurred";
      }
    },
    emitNullMeta() {
      this.$emit("entry-meta-change", {
        externalPropertyId: null,
        externalPropertyIdSource: null,
      });
    },
    emitMetaFromSelectedAddress(afAddress) {
      this.$emit("entry-meta-change", {
        externalPropertyId: afAddress?.uprn || null,
        externalPropertyIdSource: afAddress?.uprn ? "uprn" : null,
      });
    },
    handleAddressRetrieved(address) {
      if (this.addressMode === "full_address") {
        const addressLine = formatIdealPostcodesAddress(address);
        this.suppressNextMetaReset = true;
        this.$emit("address-selected", address);
        this.$emit("input", addressLine);
      } else {
        const nextValue = address?.line_1 || "";
        this.suppressNextMetaReset = true;
        this.$emit("input", nextValue);
        this.emitMetaFromSelectedAddress(address);
      }
    },
    resolveTextInputId() {
      const hostElement = document.getElementById(this.resolvedInputId);
      if (!hostElement) {
        return null;
      }
      // If the host element is already the text input, use its id directly.
      if (
        hostElement.tagName === "INPUT" &&
        hostElement.getAttribute("type") === "text"
      ) {
        return this.resolvedInputId;
      }
      // Otherwise stamp a derived id onto the nested input so AddressFinder
      // can target it directly, which lets it handle its own positioning.
      const nestedInput = hostElement.querySelector("input[type='text']");
      if (!nestedInput) {
        return null;
      }
      const derivedId = `${this.resolvedInputId}-text-input`;
      nestedInput.id = derivedId;
      return derivedId;
    },
    setupAddressFinder() {
      const textInputId = this.resolveTextInputId();
      if (!textInputId) {
        return;
      }
      this.markIdealPostcodesInput();
      const idpcApiKey = process.env.VUE_APP_IDEAL_POSTCODES_API_KEY;
      const queryOptions = {
        tags: IDPC_QUERY_TAGS,
        ...this.resolvedPostcodeOption,
      };
      const resolveOptions = { tags: IDPC_RESOLVE_TAGS };
      const countrySetting = this.resolvedDefaultCountry
        ? {
            detectCountry: false,
            defaultCountry: this.resolvedDefaultCountry,
          }
        : {};

      this.addressFinder = AddressFinder.setup({
        apiKey: idpcApiKey,
        inputField: `#${textInputId}`,
        queryOptions,
        resolveOptions,
        ...countrySetting,
        onMounted: () => (this.afAttached = true),
        onOpen: () => this.injectManualEntryOption(),
        onAddressRetrieved: (address) => this.handleAddressRetrieved(address),
      });
    },
    getInputElement() {
      const hostElement = document.getElementById(this.resolvedInputId);
      if (!hostElement) {
        return null;
      }
      if (
        hostElement.tagName === "INPUT" &&
        hostElement.getAttribute("type") === "text"
      ) {
        return hostElement;
      }
      return hostElement.querySelector("input[type='text']") || null;
    },
    detachFinder() {
      if (!this.addressFinder || !this.afAttached) {
        return;
      }
      this.addressFinder.detach();
      this.afAttached = false;
    },
    reattachFinder() {
      if (!this.afAttached) {
        this.addressFinder?.attach();
        this.afAttached = true;
        this.getInputElement()?.focus();
      }
    },
    enterManualMode() {
      this.mode = "manualAwaitRefocus";
      this.detachFinder();
      this.emitNullMeta();
      this.getInputElement()?.focus();
    },
    injectManualEntryOption() {
      const input = this.getInputElement();
      if (!input) {
        return;
      }

      // select the list nearest to this input; fall back globally.
      const root =
        input.closest(".idpc_autocomplete") ||
        input.parentElement ||
        document.body;
      const suggestionsContainer =
        root.querySelector(".idpc_ul") || document.querySelector(".idpc_ul");
      if (!suggestionsContainer) {
        return;
      }

      let manualEntry = document.getElementById(this.manualEntryId);
      if (!manualEntry) {
        manualEntry = document.createElement("div");
        manualEntry.id = this.manualEntryId;
        manualEntry.className = "idpc_manual_entry";
        manualEntry.textContent = "Manual entry";
        manualEntry.style.cssText = `
          padding: 10px;
          cursor: pointer;
          font-weight: bold;
          background: #f6f6f6;
        `;
        manualEntry.addEventListener("click", (event) => {
          event.preventDefault();
          this.enterManualMode();
        });
      }

      suggestionsContainer.after(manualEntry);
    },
  },
};
</script>

<style lang="less">
/**
 * This is needed so that the IDPC container (with position: absolute) doesn't overlap with the AntDV input.
 * Keep this override limited to inputs explicitly marked for IdealPostcodes to minimize impact.
 */
.idpc-enabled-input.ant-input,
.idpc-enabled-input .ant-input-group .ant-input,
.ant-input-group .idpc-enabled-input.ant-input {
  float: none;
}
</style>
